import { PlayerRef } from '@remotion/player';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EditModeContext } from '../edit-mode';
import { getCurrentAnimationDragData } from '../editor-property-panel/ui/contents/animation/animation-drag-data';
import { getCurrentTransitionDragData } from '../editor-property-panel/ui/contents/transition/transition-drag-data';
import { EditModeShortcut } from '../keyboard-shortcuts/edit-mode-shortcut';
import {
  getEditorZoomShortcutDirection,
  getEditorZoomShortcutTarget,
} from '../keyboard-shortcuts/editor-zoom-shortcut';
import {
  FEATURE_CANVAS_ZOOM_CONTROLS,
  FEATURE_CANVAS_ZOOM_GESTURES,
  FEATURE_CANVAS_ZOOM_KEYBOARD_SHORTCUTS,
} from '../flags';
import { MIN_TIMELINE_ZOOM } from '../constants';
import { PreviewSizeContext } from '../preview-size';
import { useTimelineZoom } from '../timeline/utils/use-timeline-zoom';
import { calculateScale } from '../utils/calculate-canvas-transformation';
import { clsx } from '../utils/clsx';
import {
  getCenterPointWhileScrolling,
  getEffectiveTranslation,
} from '../utils/get-effective-translation';
import {
  MAX_CANVAS_ZOOM,
  MIN_CANVAS_ZOOM,
  smoothenZoom,
  unsmoothenZoom,
} from '../utils/smooth-zoom';
import {
  useDimensions,
  useFps,
  useTimelineContext,
} from '../utils/use-context';
import { useElementSize } from '../utils/use-element-size';
import { useKeybinding } from '../utils/use-keybindings';
import { timelineContainerRef } from '../utils/restore-scroll-after-zoom';
import { CanvasSizeProvider } from './canvas-size';
import { ScaledPlayer } from './scaled-player';
import { useHandTool } from './use-hand-tool';
import { CanvasZoomControls } from '@/features/remotion-editor/editor/action-row/canvas-zoom-controls';

export const canvasRef = React.createRef<HTMLDivElement | null>();
const ZOOM_PX_FACTOR = 0.001;
const TIMELINE_KEYBOARD_ZOOM_STEP = 0.1;
const MAX_TIMELINE_KEYBOARD_ZOOM = 1;

export const Canvas = ({
  playerRef,
  loop,
  className,
  enableMaskEditing = false,
  autoPlay = false,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
  loop: boolean;
  className?: string;
  enableMaskEditing?: boolean;
  autoPlay?: boolean;
}) => {
  const { setSize, size: previewSize } = useContext(PreviewSizeContext);
  const isFit = previewSize.size === 'auto';

  const size = useElementSize(canvasRef, {
    triggerOnWindowResize: true,
  });

  const { durationInFrames } = useTimelineContext();
  const { compositionHeight, compositionWidth } = useDimensions();
  const { fps } = useFps();

  const onWheel = useCallback(
    (e: WheelEvent) => {
      if (!size) {
        return;
      }

      const wantsToZoom = e.ctrlKey || e.metaKey;

      if (!wantsToZoom && isFit) {
        return;
      }

      e.preventDefault();

      setSize((prevSize) => {
        const scale = calculateScale({
          canvasSize: size,
          compositionHeight,
          compositionWidth,
          previewSize: prevSize.size,
        });

        // 확대/축소
        if (wantsToZoom) {
          const oldSize = prevSize.size === 'auto' ? scale : prevSize.size;
          const smoothened = smoothenZoom(oldSize);
          const added = smoothened + e.deltaY * ZOOM_PX_FACTOR;
          const unsmoothened = unsmoothenZoom(added);

          const { centerX, centerY } = getCenterPointWhileScrolling({
            size,
            clientX: e.clientX,
            clientY: e.clientY,
            compositionWidth,
            compositionHeight,
            scale,
            translation: prevSize.translation,
          });

          const zoomDifference = unsmoothened - oldSize;

          const uvCoordinatesX = centerX / compositionWidth;
          const uvCoordinatesY = centerY / compositionHeight;

          const correctionLeft =
            -uvCoordinatesX * (zoomDifference * compositionWidth) +
            (1 - uvCoordinatesX) * zoomDifference * compositionWidth;
          const correctionTop =
            -uvCoordinatesY * (zoomDifference * compositionHeight) +
            (1 - uvCoordinatesY) * zoomDifference * compositionHeight;

          return {
            translation: getEffectiveTranslation({
              translation: {
                x: prevSize.translation.x - correctionLeft / 2,
                y: prevSize.translation.y - correctionTop / 2,
              },
              canvasSize: size,
              compositionHeight,
              compositionWidth,
              scale,
            }),
            size: unsmoothened,
          };
        }

        const effectiveTranslation = getEffectiveTranslation({
          translation: prevSize.translation,
          canvasSize: size,
          compositionHeight,
          compositionWidth,
          scale,
        });

        // 이동
        return {
          ...prevSize,
          translation: getEffectiveTranslation({
            translation: {
              x: effectiveTranslation.x + e.deltaX,
              y: effectiveTranslation.y + e.deltaY,
            },
            canvasSize: size,
            compositionHeight,
            compositionWidth,
            scale,
          }),
        };
      });
    },
    [compositionHeight, compositionWidth, isFit, setSize, size],
  );

  useEffect(() => {
    if (!FEATURE_CANVAS_ZOOM_GESTURES) {
      return;
    }

    const { current } = canvasRef;
    if (!current) {
      return;
    }

    current.addEventListener('wheel', onWheel, { passive: false });

    return () =>
      // @ts-expect-error - missing types
      current.removeEventListener('wheel', onWheel, {
        passive: false,
      });
  }, [onWheel]);

  const onReset = useCallback(() => {
    setSize(() => {
      return {
        translation: {
          x: 0,
          y: 0,
        },
        size: 'auto',
      };
    });
  }, [setSize]);

  const onZoomIn = useCallback(() => {
    if (!size) {
      return;
    }

    setSize((prevSize) => {
      const scale = calculateScale({
        canvasSize: size,
        compositionHeight,
        compositionWidth,
        previewSize: prevSize.size,
      });
      return {
        translation: {
          x: 0,
          y: 0,
        },
        size: Math.min(MAX_CANVAS_ZOOM, scale * 2),
      };
    });
  }, [compositionHeight, compositionWidth, setSize, size]);

  const onZoomOut = useCallback(() => {
    if (!size) {
      return;
    }

    setSize((prevSize) => {
      const scale = calculateScale({
        canvasSize: size,
        compositionHeight,
        compositionWidth,
        previewSize: prevSize.size,
      });
      return {
        translation: {
          x: 0,
          y: 0,
        },
        size: Math.max(MIN_CANVAS_ZOOM, scale / 2),
      };
    });
  }, [compositionHeight, compositionWidth, setSize, size]);

  const { setZoom: setTimelineZoom } = useTimelineZoom();

  const onTimelineZoomIn = useCallback(() => {
    setTimelineZoom((prev) =>
      Math.min(prev + TIMELINE_KEYBOARD_ZOOM_STEP, MAX_TIMELINE_KEYBOARD_ZOOM),
    );
  }, [setTimelineZoom]);

  const onTimelineZoomOut = useCallback(() => {
    setTimelineZoom((prev) =>
      Math.max(prev - TIMELINE_KEYBOARD_ZOOM_STEP, MIN_TIMELINE_ZOOM),
    );
  }, [setTimelineZoom]);

  const onKeyboardZoom = useCallback(
    (e: KeyboardEvent) => {
      const direction = getEditorZoomShortcutDirection({
        key: e.key,
        shiftKey: e.shiftKey,
      });
      if (!direction) {
        return;
      }

      const target = getEditorZoomShortcutTarget({
        canvas: canvasRef.current,
        timeline: timelineContainerRef.current,
      });

      if (!target) {
        return;
      }

      if (target === 'timeline') {
        if (direction === 'in') {
          onTimelineZoomIn();
        } else {
          onTimelineZoomOut();
        }
      } else if (direction === 'in') {
        onZoomIn();
      } else {
        onZoomOut();
      }

      e.preventDefault();
    },
    [onTimelineZoomIn, onTimelineZoomOut, onZoomIn, onZoomOut],
  );

  const keybindings = useKeybinding();

  useEffect(() => {
    if (!FEATURE_CANVAS_ZOOM_KEYBOARD_SHORTCUTS) {
      return;
    }

    /**
     * 0은 캔버스 뷰포트 reset 단축키이고, +/-는 마우스 hover 영역에 따라
     * 캔버스 또는 타임라인 줌으로 라우팅된다.
     * 하지만 텍스트 아이템 편집 모드(RichTextEditor, contentEditable)에서는
     * 같은 키가 실제 문자 입력으로 사용되므로 단축키보다 입력을 우선해야 한다.
     * triggerIfInputFieldFocused=false를 통해 입력 포커스 상태에서는 실행하지 않는다.
     */
    const resetBinding = keybindings.registerKeybinding({
      event: 'keydown',
      key: '0',
      commandCtrlKey: false,
      callback: onReset,
      preventDefault: true,
      triggerIfInputFieldFocused: false,
    });

    const zoomIn = keybindings.registerKeybinding({
      event: 'keydown',
      key: '+',
      commandCtrlKey: false,
      callback: onKeyboardZoom,
      preventDefault: false,
      triggerIfInputFieldFocused: false,
    });

    const zoomInEqual = keybindings.registerKeybinding({
      event: 'keydown',
      key: '=',
      commandCtrlKey: false,
      callback: onKeyboardZoom,
      preventDefault: false,
      triggerIfInputFieldFocused: false,
    });

    const zoomOut = keybindings.registerKeybinding({
      event: 'keydown',
      key: '-',
      commandCtrlKey: false,
      callback: onKeyboardZoom,
      preventDefault: false,
      triggerIfInputFieldFocused: false,
    });

    return () => {
      resetBinding.unregister();
      zoomIn.unregister();
      zoomInEqual.unregister();
      zoomOut.unregister();
    };
  }, [keybindings, onKeyboardZoom, onReset]);

  const { editMode } = useContext(EditModeContext);

  const isHandToolMode = editMode === 'hand';

  const { isPanning } = useHandTool({
    canvasRef,
    canvasSize: size,
    compositionWidth,
    compositionHeight,
    isHandToolMode,
  });

  // Transition/Animation 드래그 시 canvas로 드롭 불가 표시
  const [isTransitionDragging, setIsTransitionDragging] = useState(false);
  const [isAnimationDragging, setIsAnimationDragging] = useState(false);

  // 전역 드래그 이벤트 감지
  useEffect(() => {
    const handleGlobalDragOver = () => {
      const transitionData = getCurrentTransitionDragData();
      if (transitionData && !isTransitionDragging) {
        setIsTransitionDragging(true);
      }
      const animationData = getCurrentAnimationDragData();
      if (animationData && !isAnimationDragging) {
        setIsAnimationDragging(true);
      }
    };

    const handleGlobalDragEnd = () => {
      setIsTransitionDragging(false);
      setIsAnimationDragging(false);
    };

    const handleGlobalDrop = () => {
      setIsTransitionDragging(false);
      setIsAnimationDragging(false);
    };

    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('dragend', handleGlobalDragEnd);
    window.addEventListener('drop', handleGlobalDrop);

    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('dragend', handleGlobalDragEnd);
      window.removeEventListener('drop', handleGlobalDrop);
    };
  }, [isTransitionDragging, isAnimationDragging]);

  // 오버레이 드래그 이벤트 핸들러
  const handleOverlayDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  }, []);

  const handleOverlayDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsTransitionDragging(false);
    setIsAnimationDragging(false);
  }, []);

  return (
    <div
      className={clsx(
        'relative flex flex-1 overflow-hidden bg-sys-bg-surface-2 select-none',
        isPanning && 'cursor-grabbing [&_*]:!cursor-grabbing',
        !isPanning && editMode === 'hand' && 'cursor-grab [&_*]:!cursor-grab',
        !isPanning && editMode === 'draw-solid' && 'cursor-crosshair',
        !isPanning && editMode === 'draw-blur' && 'cursor-crosshair',
        !isPanning && editMode === 'create-text' && 'cursor-text',
        className,
      )}
    >
      <div
        className="relative flex flex-1"
        ref={canvasRef}
      >
        <CanvasSizeProvider size={size}>
          <ScaledPlayer
            compositionHeight={compositionHeight}
            compositionWidth={compositionWidth}
            loop={loop}
            playerRef={playerRef}
            fps={fps}
            canvasSize={size}
            framesShownInTimeline={durationInFrames}
            canvasRef={canvasRef}
            enableMaskEditing={enableMaskEditing}
            autoPlay={autoPlay}
          />
        </CanvasSizeProvider>
        {FEATURE_CANVAS_ZOOM_CONTROLS ? (
          <div
            className={'absolute top-2 right-2 flex min-h-[28px] min-w-[104px]'}
          >
            <div>
              <CanvasZoomControls />
            </div>
          </div>
        ) : null}
      </div>
      <EditModeShortcut />
      {/* Transition/Animation 드래그 시 드롭 불가 오버레이 */}
      {(isTransitionDragging || isAnimationDragging) && (
        <div
          className="absolute inset-0 z-50 flex cursor-not-allowed items-center justify-center bg-black/20"
          onDragOver={handleOverlayDragOver}
          onDrop={handleOverlayDrop}
        >
          <div className="rounded-md bg-black/70 px-4 py-2 text-white">
            타임라인에만 드롭할 수 있습니다
          </div>
        </div>
      )}
    </div>
  );
};
